import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Segmented, Spin } from 'antd';
import { Button } from '@common/musicHubCommon';
import { useUserStore, useGenreStore, fetchGenresIfNeeded } from '../../../store';
import { ROUTES } from '../../../routes';
import { reportPageView } from '../../../services/reportLog';
import { fetchArtistDashboard } from './service';
import type { ArtistStats, DashboardMusicItem } from '@services/types';
import { TIME_RANGE_OPTIONS, type TimeRange } from './constant';

const SEC_PER_DAY = 86400;

/** 按发行时间 publishTime（秒）筛选；无有效时间则在非 All 下不展示 */
function filterMusicListByTimeRange(list: DashboardMusicItem[], range: TimeRange): DashboardMusicItem[] {
  if (range === 'all') return list;
  const days = range === '7d' ? 7 : 30;
  const cutoff = Math.floor(Date.now() / 1000) - days * SEC_PER_DAY;
  return list.filter((item) => {
    const t = item.publishTime;
    if (t == null || !Number.isFinite(t) || t <= 0) return false;
    return t >= cutoff;
  });
}
import musicEmptySvg from './assets/music-empty.svg';
import { ShareSongDialog } from './ShareSongDialog';
import './index.scss';

const SHARE_ICON_CDN = 'https://akoss.bangcdn.net/fe/musichubshare.png';

/** 无数据占位（双短横） */
const STAT_PLACEHOLDER = '--';

interface SongStatsItem {
  id: string;
  title: string;
  subtitle: string;
  cover?: string;
  /** null 表示无数据 → `--` */
  plays: number | null;
  likes: number | null;
  shares: number | null;
  tags: string[];
  releaseDate: string;
}

/** 后端 publishTime 为 Unix 秒；稿面 Release: DD-MM-YYYY */
function formatDateDDMMYYYY(tsSeconds?: number) {
  if (!tsSeconds) return '';
  try {
    const d = new Date(tsSeconds * 1000);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    return `${dd}-${mm}-${yyyy}`;
  } catch {
    return '';
  }
}

/** 统计数值：≥1000 用 K（最多一位小数），如 45900 → 45.9K */
function formatStatCount(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '0';
  if (n < 1000) return Math.round(n).toLocaleString();
  const k = n / 1000;
  const rounded = Math.round(k * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text}K`;
}

/** 接口缺字段 / null / 空串 / NaN → 无数据 */
function parseOptionalDashboardStat(v: unknown): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'string' && v.trim() === '') return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return n;
}

function formatStatCountOrPlaceholder(n: number | null): string {
  if (n === null) return STAT_PLACEHOLDER;
  return formatStatCount(n);
}

interface StatCardProps {
  label: string;
  value: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value }) => (
  <div className="stat-card">
    <span className="stat-card__label">{label}</span>
    <span className="stat-card__value">{value}</span>
  </div>
);

interface SongStatsCardProps {
  song: SongStatsItem;
  onShare: (song: SongStatsItem) => void;
}

/** 分享图标 — 设计稿 24×24，CDN 资源 */
const ShareIcon = () => (
  <img src={SHARE_ICON_CDN} alt="" width={24} height={24} className="song-stats-card__share-icon" />
);

const SongStatsCard: React.FC<SongStatsCardProps> = ({ song, onShare }) => (
  <div className="song-stats-card">
    <div className="song-stats-card__header">
      <div className="song-stats-card__cover">
        {song.cover ? (
          <img src={song.cover} alt={song.title} />
        ) : (
          <div className="song-stats-card__cover-placeholder">
            <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" />
            </svg>
          </div>
        )}
      </div>
      <div className="song-stats-card__info">
        <div className="song-stats-card__title-row">
          <h3 className="song-stats-card__title">{song.title}</h3>
          <Button
            type="text"
            className="song-stats-card__share-btn"
            aria-label="Share"
            onClick={() => onShare(song)}
          >
            <ShareIcon />
          </Button>
        </div>
        <p className="song-stats-card__album">{song.subtitle}</p>
        <div className="song-stats-card__tags">
          {song.tags.map((tag) => (
            <span key={tag} className="song-stats-card__tag">
              {tag}
            </span>
          ))}
        </div>
        <p className="song-stats-card__release">
          Release: {song.releaseDate || STAT_PLACEHOLDER}
        </p>
      </div>
    </div>
    <div className="song-stats-card__stats">
      <div className="song-stats-card__stat">
        <span className="song-stats-card__stat-label">Plays</span>
        <span className="song-stats-card__stat-value">{formatStatCountOrPlaceholder(song.plays)}</span>
      </div>
      <div className="song-stats-card__stat-divider" />
      <div className="song-stats-card__stat">
        <span className="song-stats-card__stat-label">Likes</span>
        <span className="song-stats-card__stat-value">{formatStatCountOrPlaceholder(song.likes)}</span>
      </div>
      <div className="song-stats-card__stat-divider" />
      <div className="song-stats-card__stat">
        <span className="song-stats-card__stat-label">Shares</span>
        <span className="song-stats-card__stat-value">{formatStatCountOrPlaceholder(song.shares)}</span>
      </div>
    </div>
  </div>
);

const UploadIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 16h6v-6h4l-7-7-7 7h4v6zm-4 2h14v2H5v-2z" />
  </svg>
);

const Dashboard: React.FC = () => {
  const user = useUserStore((state) => state.user);
  const genreOptions = useGenreStore((s) => s.genreOptions);
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('all');
  const [loading, setLoading] = useState(false);
  const [artistStats, setArtistStats] = useState<ArtistStats>({
    plays: 0,
    likes: 0,
    shares: 0,
    downloads: 0,
  });
  const [musicList, setMusicList] = useState<DashboardMusicItem[]>([]);
  const [shareSong, setShareSong] = useState<SongStatsItem | null>(null);

  const genreLabelById = useMemo(() => {
    const m = new Map<string, string>();
    genreOptions.forEach((g) => m.set(g.value, g.label));
    return m;
  }, [genreOptions]);

  useEffect(() => {
    reportPageView('content_0007', { withArtistId: true });
  }, []);

  useEffect(() => {
    void fetchGenresIfNeeded();
  }, []);

  useEffect(() => {
    const artistID = user?.id || '';
    if (!artistID) return;

    setLoading(true);
    fetchArtistDashboard(artistID)
      .then((data) => {
        setArtistStats(data.artistStats);
        setMusicList(data.musicList);
      })
      .catch(() => {
        setArtistStats({ plays: 0, likes: 0, shares: 0, downloads: 0 });
        setMusicList([]);
      })
      .finally(() => setLoading(false));
  }, [user?.id]);

  const songs: SongStatsItem[] = useMemo(() => {
    const filtered = filterMusicListByTimeRange(musicList, timeRange);
    return filtered.map((item) => {
      const genreIds = Array.isArray(item.genre) ? item.genre : [];
      const tags = genreIds.map((id) => genreLabelById.get(String(id)) || String(id));
      const subtitle =
        item.producer?.trim() ||
        (item.featuredArtist?.length ? item.featuredArtist.join(', ') : STAT_PLACEHOLDER);
      return {
        id: item.musicID,
        title: item.title || '',
        subtitle,
        cover: item.cover || undefined,
        plays: parseOptionalDashboardStat(item.plays),
        likes: parseOptionalDashboardStat(item.likes),
        shares: parseOptionalDashboardStat(item.shares),
        tags,
        releaseDate: formatDateDDMMYYYY(item.publishTime),
      };
    });
  }, [musicList, genreLabelById, timeRange]);

  const segmentedOptions = TIME_RANGE_OPTIONS.map((o) => ({
    label: o.label,
    value: o.value,
  }));

  return (
    <div className="dashboard">
      <div className="dashboard__hero">
        <div className="hero-overlay" />
        <div className="hero-content">
          <h1 className="hero-content__name">{user?.name || 'Artist'}</h1>
        </div>
      </div>

      <div className="dashboard__mobile-release">
        <Button type="primary" className="release-btn" onClick={() => navigate(ROUTES.releaseType)}>
          <UploadIcon />
          <span>Release Song</span>
        </Button>
      </div>

      <section className="dashboard__section">
        <h2 className="section-title">Total Stats</h2>
        <Spin spinning={loading}>
          <div className="stats-grid">
            <StatCard label="Plays" value={formatStatCount(artistStats.plays)} />
            <div className="stats-divider" />
            <StatCard label="Likes" value={formatStatCount(artistStats.likes)} />
            <div className="stats-divider" />
            <StatCard label="Shares" value={formatStatCount(artistStats.shares)} />
            <div className="stats-divider" />
            <StatCard label="Downloads" value={STAT_PLACEHOLDER} />
          </div>
        </Spin>
      </section>

      <section className="dashboard__section">
        <div className="section-header">
          <h2 className="section-title">Song Performance</h2>
          <Segmented
            className="dashboard__segmented"
            options={segmentedOptions}
            value={timeRange}
            onChange={(v) => setTimeRange(v as TimeRange)}
          />
        </div>
        <Spin spinning={loading}>
          <div className="songs-grid">
            {songs.length > 0 ? (
              songs.map((song) => (
                <SongStatsCard key={song.id} song={song} onShare={setShareSong} />
              ))
            ) : (
              <div className="songs-empty">
                <div className="songs-empty__icon">
                  <img src={musicEmptySvg} alt="" width={64} height={64} />
                </div>
                <p className="songs-empty__text">No Songs</p>
              </div>
            )}
          </div>
        </Spin>
      </section>

      <ShareSongDialog
        open={Boolean(shareSong)}
        onClose={() => setShareSong(null)}
        songTitle={shareSong?.title ?? ''}
        artistName={user?.name || 'Artist'}
        musicId={shareSong?.id ?? ''}
      />
    </div>
  );
};

export default Dashboard;
